import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  SUPPORT_HANDOFF_TTL_SECONDS,
  SUPPORT_SESSION_DEFAULT_MINUTES,
  type SignInAsResultDto,
  type SupportAccessLogDto,
} from '@his/shared';
import { get, post } from '@/lib/api';
import { describeError } from '@/lib/auth';
import { dateTime } from '@/lib/utils';
import { ChoiceButtons } from '@/components/ui/choice-buttons';
import { DataTable, Dialog } from '@/components/ui/layout';
import { Alert, Badge, Button, Card, CardContent, CardDescription, CardHeader, CardTitle, Field } from '@/components/ui/primitives';
import { useToast } from '@/components/ui/toast';
import { OwnerConfirmDialog } from './OwnerConfirm';

const MINUTE_CHOICES = [15, 30, 45, 60];

export function SignInAsDialog({
  tenantId,
  user,
  onClose,
}: {
  tenantId: string;
  user: { id: string; name: string };
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const [minutes, setMinutes] = useState(SUPPORT_SESSION_DEFAULT_MINUTES);
  const [link, setLink] = useState<SignInAsResultDto | null>(null);

  return (
    <>
      {link && (
        <SignInLinkDialog
          link={link}
          onClose={() => {
            void queryClient.invalidateQueries({ queryKey: ['tenant-support-access', tenantId] });
            onClose();
          }}
        />
      )}
      {!link && (
        <OwnerConfirmDialog
          open
          onClose={onClose}
          title={`Sign in as ${user.name}`}
          description="Opens the facility in a new tab, read-only, with no clinical records."
          warning="The facility's Super Admins are told afterwards, and the session appears in their support access log with the reason you give. Nothing can be changed in the session except approving a new branch."
          confirmLabel="Start session"
          successMessage="Session ready"
          action={(payload) => post<SignInAsResultDto>(`/platform/tenants/${tenantId}/sign-in-as`, { ...payload, userId: user.id, minutes })}
          onDone={(result) => setLink(result)}
        >
          <Field label="Session length">
            <ChoiceButtons
              value={String(minutes)}
              onChange={(value) => setMinutes(Number(value))}
              options={MINUTE_CHOICES.map((choice) => ({ value: String(choice), label: `${choice} minutes` }))}
            />
          </Field>
        </OwnerConfirmDialog>
      )}
    </>
  );
}

function SignInLinkDialog({ link, onClose }: { link: SignInAsResultDto; onClose: () => void }) {
  const [secondsLeft, setSecondsLeft] = useState(SUPPORT_HANDOFF_TTL_SECONDS);
  const [opened, setOpened] = useState(false);

  useEffect(() => {
    const deadline = new Date(link.expiresAt).getTime();
    const timer = window.setInterval(() => {
      const left = Math.max(0, Math.round((deadline - Date.now()) / 1000));
      setSecondsLeft(left);
      if (left === 0) window.clearInterval(timer);
    }, 1_000);
    return () => window.clearInterval(timer);
  }, [link.expiresAt]);

  const open = () => {
    window.open(`${window.location.origin}${link.handoffPath}`, '_blank', 'noopener');
    setOpened(true);
  };

  return (
    <Dialog open onClose={onClose} title={`Open the facility as ${link.user.name}`} description={`Session ends at ${dateTime(link.endsAt)}.`}>
      <div className="space-y-4 text-sm">
        {secondsLeft > 0 && !opened && <p>The link works once and expires in {secondsLeft} s.</p>}
        {secondsLeft === 0 && !opened && <Alert variant="warning">The link has expired. Start a new session.</Alert>}
        {opened && <p className="text-muted-foreground">The facility opened in a new tab. Use End session there when you are done.</p>}
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
          <Button onClick={open} disabled={opened || secondsLeft === 0}>
            Open in a new tab
          </Button>
        </div>
      </div>
    </Dialog>
  );
}

export function TenantSupportAccessCard({ tenantId, canEnd }: { tenantId: string; canEnd: boolean }) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const log = useQuery({
    queryKey: ['tenant-support-access', tenantId],
    queryFn: () => get<SupportAccessLogDto>(`/platform/tenants/${tenantId}/support-access`),
  });
  const end = useMutation({
    mutationFn: (sessionId: string) => post(`/platform/tenants/${tenantId}/sign-in-as/${sessionId}/end`),
    onSuccess: () => {
      toast.success('Session ended');
      void queryClient.invalidateQueries({ queryKey: ['tenant-support-access', tenantId] });
    },
    onError: (error) => toast.error('Could not end the session', describeError(error)),
  });

  return (
    <Card className="mt-4">
      <CardHeader>
        <CardTitle>Owner sign-ins</CardTitle>
        <CardDescription>Every time the Owner signed in to this facility. The facility sees the same list.</CardDescription>
      </CardHeader>
      <CardContent>
        <DataTable
          rows={log.data?.entries}
          loading={log.isLoading} refreshing={log.isFetching}
          error={log.error}
          rowKey={(row) => row.id}
          empty="The Owner has never signed in to this facility."
          columns={[
            { header: 'Started', cell: (row) => dateTime(row.startedAt) },
            { header: 'By', cell: (row) => row.by },
            { header: 'As', cell: (row) => row.userName ?? '—' },
            { header: 'Reason', cell: (row) => row.reason },
            {
              header: 'Ended',
              cell: (row) => (row.active ? <Badge variant="warning">Open</Badge> : row.endedAt ? dateTime(row.endedAt) : '—'),
            },
            {
              header: '',
              align: 'right',
              cell: (row) =>
                canEnd && row.active ? (
                  <Button size="sm" variant="outline" onClick={() => end.mutate(row.id)} loading={end.isPending && end.variables === row.id}>
                    End
                  </Button>
                ) : null,
            },
          ]}
        />
      </CardContent>
    </Card>
  );
}
